// Searchable multi-select DROPDOWN used inside the appointment filter dialog.
// Trigger shows a summary; the panel lists rows with checkbox + optional
// thumbnail + title (+ subtitle). At least one option always stays selected.

import { useMemo, useState } from 'react';
import { Search, Check, CalendarClock, ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { Popover } from './Popover';

export interface CheckOption {
  value: string;
  label: string;
  subtitle?: string;
  image?: string | null;
}

interface Props {
  label: string;
  options: CheckOption[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  withImage?: boolean;
  searchPlaceholder?: string;
}

const FilterCheckList = ({ label, options, selected, onChange, withImage, searchPlaceholder = 'Search…' }: Props) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () => options.filter((o) => !q || o.label.toLowerCase().includes(q) || (o.subtitle || '').toLowerCase().includes(q)),
    [options, q],
  );
  const allSelected = options.length > 0 && selected.size === options.length;

  const toggle = (v: string) => {
    const n = new Set(selected);
    if (n.has(v)) {
      if (n.size === 1) return; // keep at least one
      n.delete(v);
    } else n.add(v);
    onChange(n);
  };
  const toggleAll = () =>
    onChange(allSelected ? new Set([options[0]?.value].filter(Boolean) as string[]) : new Set(options.map((o) => o.value)));

  const summary = allSelected
    ? `All ${label.toLowerCase()}`
    : selected.size === 1
      ? options.find((o) => o.value === Array.from(selected)[0])?.label ?? '1 selected'
      : `${selected.size} selected`;

  return (
    <div>
      <label className="block text-[12px] font-semibold uppercase tracking-wide text-text-muted mb-2">{label}</label>
      <Popover
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}
        panelClassName="w-full min-w-full"
        trigger={
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="w-full h-10 rounded-lg border border-border bg-surface px-3 flex items-center justify-between gap-2 text-[14px] cursor-pointer hover:border-primary/50 transition-colors"
          >
            <span className={clsx('truncate', allSelected ? 'text-text-muted' : 'text-text-primary font-medium')}>{summary}</span>
            <ChevronDown size={14} className={clsx('text-text-muted shrink-0 transition-transform', open && 'rotate-180')} />
          </button>
        }
      >
        {/* search + select-all */}
        <div className="p-1.5 border-b border-border">
          <div className="flex items-center gap-2 rounded-lg bg-surface-alt px-2.5 h-9 transition-colors">
            <Search className="h-3.5 w-3.5 shrink-0 text-text-muted" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-full w-full bg-transparent text-[14px] text-text-primary outline-none placeholder:text-text-muted"
            />
            <button type="button" onClick={toggleAll} className="text-[12px] font-semibold text-primary border-none bg-transparent cursor-pointer shrink-0 whitespace-nowrap">
              {allSelected ? 'Clear' : 'All'}
            </button>
          </div>
        </div>
        {/* rows */}
        <div className="max-h-[220px] overflow-y-auto p-1 flex flex-col gap-0.5">
          {filtered.map((o) => {
            const on = selected.has(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggle(o.value)}
                className="flex items-center gap-2.5 w-full min-w-0 px-2 py-2 rounded-md border-none cursor-pointer text-left transition-colors bg-transparent hover:bg-surface-alt"
              >
                <span className={clsx('w-[17px] h-[17px] rounded border flex items-center justify-center shrink-0', on ? 'bg-primary border-primary' : 'border-border-strong')}>
                  {on && <Check size={12} strokeWidth={3} className="text-on-primary" />}
                </span>
                {withImage && (
                  o.image ? (
                    <img src={o.image} alt="" className="w-8 h-8 rounded-md object-cover object-top shrink-0" />
                  ) : (
                    <span className="w-8 h-8 rounded-md bg-primary-soft text-primary flex items-center justify-center shrink-0"><CalendarClock size={15} /></span>
                  )
                )}
                <span className={clsx('min-w-0 flex-1', on ? 'opacity-100' : 'opacity-70')}>
                  <span className="block text-[14px] font-semibold text-text-primary truncate leading-tight" title={o.label}>{o.label}</span>
                  {o.subtitle && (
                    <span className="block text-[12.5px] text-text-muted truncate leading-tight" title={o.subtitle}>{o.subtitle}</span>
                  )}
                </span>
              </button>
            );
          })}
          {filtered.length === 0 && <div className="px-2 py-4 text-center text-[12.5px] text-text-muted">No matches.</div>}
        </div>
      </Popover>
    </div>
  );
};

export default FilterCheckList;
